import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CSTypes, GetResponse } from '@tf/api';
import { GraphQLClient, gql, PublicApiClient } from '@tf/api';
import { Alert, Button, Group, TFText, useLocalStorage } from '@tf/ui';
import { createPublicSchemaId } from '@tf/utils';
import type React from 'react';
import { useEffect } from 'react';

import { useClusterId } from '@/core/appMeta';
import { appStore } from '@/core/stores';

const previewParam = 'preview-configuration';
const lsPreviewParam = `tf-${previewParam}`;

export type IRPatch = {
	// TODO: add "comment" to fields; Only for dev-export: we don't need it for sharing (?)
	//		Or add comments straight to YAML
	structs: Record<string, CSTypes.StructDefinition>;
	forms: CSTypes.FormDefinition[];
	options: Record<string, CSTypes.SelectOption[]>;
};

export const getPreviewLink = (configurationPreviewId: string) => {
	return `${window.location.origin}/?${previewParam}=${configurationPreviewId}`;
};

export const useConfigurationPreview = () => {
	const [value, setValue, removeValue] = useLocalStorage({
		key: lsPreviewParam,
		defaultValue: '',
		getInitialValueInEffect: false,
	});

	return {
		isEnabled: !!value,
		irPatchId: value,
		enable: (configurationPreviewId: string) => setValue(configurationPreviewId),
		disable: removeValue,
	};
};

export const useEnableConfigurationPreview = () => {
	const { enable } = useConfigurationPreview();

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const configurationPreviewId = searchParams.get(previewParam);

		if (configurationPreviewId) {
			enable(configurationPreviewId);
		}
	}, [enable]);
};

export const ConfigurationPreviewNotification: React.FC = () => {
	const { isEnabled, disable } = useConfigurationPreview();

	if (!isEnabled) {
		return null;
	}

	const exitPreviewMode = () => {
		disable();
		window.location.reload();
	};

	return (
		<Alert color="blue">
			<Group gap="16px" align="center" wrap="nowrap">
				<TFText>Preview mode is on. You can view new form fields, but changes won’t be saved</TFText>
				<Button sx={{ flexShrink: 0 }} variant="subtle" onClick={exitPreviewMode}>
					Exit preview mode
				</Button>
			</Group>
		</Alert>
	);
};

export type IrPatchInfo = {
	id: string;
	meta: {
		'@filename': string;
		'@mime': string;
		'@size': number;
		'@createdAt': string;
	};
};

const existingIRPatchesQuery = () => {
	return queryOptions({
		queryKey: ['irPatches'],
		queryFn: async () => {
			const irPatchesRes = await GraphQLClient.getInstance().request<GetResponse<IrPatchInfo>>(
				gql`
					query ExistingIRPatches {
						get(
							rootFilter: { kind: "ir_patch" }
							paginationFilter: { sortByMeta: "/@createdAt", sortByDirection: DESC }
						) {
							id
							meta(path: ["/@filename", "/@mime", "/@size", "/@createdAt"])
						}
					}
				`,
			);
			return irPatchesRes.get;
		},
	});
};

export const useExistingIRPatches = () => {
	return useQuery(existingIRPatchesQuery());
};

export const useUploadIRPatchMutation = () => {
	const queryClient = useQueryClient();
	const clusterId = useClusterId();

	return useMutation({
		mutationFn: async ({ irPatch, filename }: { irPatch: IRPatch; filename: string }) => {
			const file = new File([JSON.stringify(irPatch)], `${filename}.json`, { type: 'application/json' });

			const result = await GraphQLClient.getInstance().request<
				{
					mutate: { id: string; kind: string }[];
				},
				{ files: any[] }
			>(
				gql`
			mutation UploadFile($files: [Upload!]!) {
				mutate(
					create: [
						{
							id: "new://file"
							schema: "${createPublicSchemaId({ clusterId, schemaTag: 'types/general/file/ir_patch' })}"
							files: $files
							upload: { _multipart_id: "file1" }
						}
					]
				) {
					id
					kind
				}
			}
		`,
				{
					files: [file],
				},
			);

			return result.mutate.find((r) => r.kind === 'ir_patch')?.id ?? null;
		},
		onSuccess: () => {
			queryClient.invalidateQueries(existingIRPatchesQuery());
		},
	});
};

const getIRPatch = async (irPathObjectName: string): Promise<IRPatch | null> => {
	const irPatchRes = await GraphQLClient.getInstance().request<
		GetResponse<{ id: string }>,
		{ irPathObjectName: string }
	>(
		gql`
			query GetIRPatch($irPathObjectName: String!) {
				get(rootFilter: { kind: "ir_patch", uuid: $irPathObjectName }) {
					id
				}
			}
		`,
		{ irPathObjectName },
	);

	if (irPatchRes.get.length === 0) {
		return null;
	}

	const fileRes = await PublicApiClient.getInstance().request({
		path: `/v1/data/general_graph/object/${encodeURIComponent(irPatchRes.get[0].id)}/download`,
	});

	return fileRes.json();
};

export const ApplyConfigurationPreviewPatch = () => {
	const { isEnabled, irPatchId } = useConfigurationPreview();

	useEffect(() => {
		if (!isEnabled) {
			return;
		}

		const applyPatch = async () => {
			const irPatch = await getIRPatch(irPatchId);
			if (irPatch) {
				appStore.set((prev) => {
					return {
						...prev,
						defs: {
							...prev.defs,
							// we can replace forms
							forms: irPatch.forms,
							// but need to merge structs and options because they have some system items that were not exported and imported
							structs: { ...prev.defs.structs, ...irPatch.structs },
							options: { ...prev.defs.options, ...irPatch.options },
						},
					};
				});
			}
		};

		applyPatch();
	}, [irPatchId, isEnabled]);

	return null;
};
